import { useEffect, useState } from "react";
import { BottomButtonContainer, KipaButton } from "@/components/kipa";
import KipaIcon from "@/components/kipa/KipaIcon";
import { BarChart3, Briefcase, MessageSquare, Receipt } from "lucide-react";

const SLIDES = [
  {
    icon: Receipt,
    headline: "Finally know your real profit",
    body: "Most Nigerian business owners don't know if they made money today. KipaBiz tells you in real time — without spreadsheets or accountants.",
    bgClass: "onb-slide-bg-1",
    iconClass: "onb-icon-box-1",
    headlineClass: "onb-headline-1",
    accent: "var(--color-success)",
    showSkip: true,
  },
  {
    icon: MessageSquare,
    headline: "Just type what happened",
    body: "Tell your AI accountant what you sold or spent — in plain language, like texting a friend. Invoices, profit tracking, and reports happen automatically.",
    bgClass: "onb-slide-bg-2",
    iconClass: "onb-icon-box-2",
    headlineClass: "onb-headline-2",
    accent: "var(--color-gold)",
    showSkip: true,
  },
  {
    icon: BarChart3,
    headline: "Your business has never been this clear",
    body: "Daily summaries. Automatic invoices. Stock alerts. Professional reports. Everything your business needs — handled.",
    bgClass: "onb-slide-bg-3",
    iconClass: "onb-icon-box-3",
    headlineClass: "onb-headline-3",
    accent: "var(--color-primary)",
    showSkip: false,
  },
];

function Splash() {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center gap-5 relative overflow-hidden min-h-screen"
      style={{ background: "var(--color-primary)" }}
      data-testid="preauth-splash"
    >
      <div className="relative z-10 flex flex-col items-center gap-4">
        <div className="kipa-flat-logo flex items-center justify-center">
          <KipaIcon icon={Briefcase} size="xl" color="#fff" />
        </div>
        <div className="text-center">
          <div className="text-white text-[var(--text-display)] font-black tracking-tight leading-none">
            KipaBiz
          </div>
          <div className="text-white/80 text-[var(--text-xs)] mt-2 tracking-[3px] uppercase font-semibold">
            Know your numbers. Grow your business.
          </div>
        </div>
      </div>
    </div>
  );
}

function ValueSlide({ slide, index, onContinue, onSkip }) {
  return (
    <div
      className={`flex-1 flex flex-col min-h-screen ${slide.bgClass}`}
      data-testid={`preauth-slide-${index + 1}`}
    >
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-8 gap-6">
        <div className={`onb-icon-box ${slide.iconClass} flex items-center justify-center`}>
          <KipaIcon icon={slide.icon} size="xl" color={slide.accent} />
        </div>
        <div className="text-center max-w-[320px]">
          <h2
            className={`text-[var(--text-2xl)] font-black mb-3 leading-tight ${slide.headlineClass}`}
          >
            {slide.headline}
          </h2>
          <p className="text-[var(--text-base)] text-[var(--color-text-secondary)] leading-relaxed">
            {slide.body}
          </p>
        </div>
        <div className="flex gap-2">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`onb-dot ${i === index ? "onb-dot-active" : "onb-dot-inactive"}`}
              style={{
                background: i === index ? slide.accent : undefined,
              }}
            />
          ))}
        </div>
      </div>
      <BottomButtonContainer className="flex flex-col gap-2">
        <KipaButton
          variant="primary"
          fullWidth
          label={index === 2 ? "Get Started" : "Continue"}
          onPress={onContinue}
          testId={`preauth-continue-${index + 1}`}
        />
        {slide.showSkip && (
          <KipaButton
            variant="ghost"
            fullWidth
            label="Skip intro"
            onPress={onSkip}
            testId={`preauth-skip-${index + 1}`}
          />
        )}
      </BottomButtonContainer>
    </div>
  );
}

/**
 * Pre-signup flow: splash (2.5s) → 3 value slides → auth screen.
 */
export default function PreAuthFlow({ onComplete }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (step === 0) {
      const t = setTimeout(() => setStep(1), 2500);
      return () => clearTimeout(t);
    }
  }, [step]);

  if (step === 0) return <Splash />;
  if (step >= 1 && step <= 3) {
    const slide = SLIDES[step - 1];
    return (
      <ValueSlide
        slide={slide}
        index={step - 1}
        onContinue={() => (step === 3 ? onComplete() : setStep(step + 1))}
        onSkip={() => onComplete()}
      />
    );
  }
  return null;
}
